import {
  Box,
  CircularProgress,
  InputAdornment,
  TextField,
  Typography
} from '@mui/material';
import React, { useEffect, useState } from 'react';
import {
  AllocationType,
  ProjectMemberAllocationGrid
} from './project-member-allocation-grid';
import { useQuery } from '@apollo/client';
import { GET_CHECK_APPROVAL, GET_PROJECT_MEMBER_ALLOCATION } from '../gql';
import ApprovalText from '../../common/widgets/approval-text';
import { IProjectAllocatedHour, IProjectMembersAllocationData } from '../type';
import CheckBoxComponent from '../../common/widgets/check-box';

interface AllocationProps {
  id?: string;
  memberId: string;
  projectId?: string;
  isAdd: boolean;
  setSuggestedAllocation?: (value: string) => void;
  setSuggestedAllocationList?: (value: AllocationType[]) => void;
  value?: string;
  onChange?: (newValue: string) => void;
  touched?: boolean;
  errors?: string;
  pendingRequest?: boolean;
  setIsMaximumAllocation?: (value: boolean) => void;
  disabled?: boolean;
}

const Allocation = ({
  id,
  memberId,
  projectId,
  isAdd,
  setSuggestedAllocation,
  setSuggestedAllocationList,
  value,
  onChange,
  touched,
  errors,
  pendingRequest,
  setIsMaximumAllocation,
  disabled
}: AllocationProps): JSX.Element => {
  const [currentAllocationProjects, setCurrentAllocationProjects] = useState<
    IProjectMembersAllocationData[]
  >([]);
  const [unit, setUnit] = useState<number>(0);
  const [isMoreThanMaximumAllocation, setIsMoreThanMaximumAllocation] =
    useState(false);
  const { loading, data: projectMemberAllocation } = useQuery(
    GET_PROJECT_MEMBER_ALLOCATION,
    {
      variables: { memberId, projectId }
    }
  );

  const [checkApprovalForOthers, setApprovalForOthers] =
    useState<boolean>(false);

  const { data: approval } = useQuery(GET_CHECK_APPROVAL, {
    variables: {
      memberId
    }
  });

  useEffect(() => {
    if (projectMemberAllocation) {
      setCurrentAllocationProjects(
        projectMemberAllocation.getProjectAllocatedHours.map(
          (item: IProjectAllocatedHour) => ({
            id: item.id,
            projectId: item.project.id,
            projectName: item.project.projectName,
            hoursPerWeek: item.hoursPerWeek.toString(),
            colorCode: item.project.colorCode,
            suggestedAllocation: ''
          })
        )
      );
    }
  }, [projectMemberAllocation]);

  useEffect(() => {
    if (approval) {
      if (approval.checkApproval === 'true') {
        setApprovalForOthers(true);
      } else {
        setApprovalForOthers(false);
      }
    }
  }, [approval]);

  const handleChange = (event: React.ChangeEvent<HTMLInputElement>): void => {
    const newValue = event.target.value;
    const onlyNumbers = newValue.replace(/[^0-9]/g, '');
    handleChangeProjectMemberAllocation({ target: { value: onlyNumbers } });
    if (onChange) {
      onChange(onlyNumbers);
    }
  };

  const handleChangeProjectMemberAllocation = (event: {
    target: { value: string };
  }): string => {
    const maxValue = 40;

    const remainingHours = maxValue - Number(event.target.value);

    let totalCurrentAllocation = 0;
    for (const project of currentAllocationProjects) {
      totalCurrentAllocation += Number(project.hoursPerWeek);
    }

    if (
      Number(event.target.value) + totalCurrentAllocation > maxValue &&
      Number(event.target.value) <= maxValue
    ) {
      setUnit(remainingHours / totalCurrentAllocation);
    } else {
      setUnit(0);
    }

    return event.target.value;
  };

  return (
    <Box
      sx={{
        padding: '26px, 42px, 26px, 42px',
        borderRadius: '20px',
        background: '#DADFED',
        paddingBottom: '10px'
      }}
    >
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          paddingBottom: '20px',
          paddingLeft: '35px',
          gap: '33px'
        }}
      >
        <Typography
          sx={{
            fontFamily: 'Be Vietnam Pro',
            fontWeight: 500,
            fontSize: '16px',
            color: '#001E2F',
            marginTop: '25px'
          }}
        >
          Allocation
        </Typography>
        <Box sx={{ maxWidth: '220px', marginTop: '25px' }}>
          <Box>
            <TextField
              id={id}
              disabled={(pendingRequest ?? checkApprovalForOthers) || disabled}
              hiddenLabel
              variant="filled"
              value={value}
              onChange={handleChange}
              sx={{
                backgroundColor: '#FFFFFF'
              }}
              InputProps={{
                endAdornment: (
                  <InputAdornment
                    sx={{
                      fontFamily: 'Be Vietnam Pro',
                      fontWeight: 400,
                      fontSize: '16px'
                    }}
                    position="end"
                  >
                    hours per week
                  </InputAdornment>
                )
              }}
              fullWidth
            />
            <Typography
              sx={{
                color: 'red',
                fontSize: '10px'
              }}
            >
              {touched && errors}
            </Typography>
          </Box>
        </Box>

        <Box sx={{ marginTop: '5px', marginRight: '30px' }}>
          <CheckBoxComponent
            id="is-maximum-allocation"
            label={'Allocate more than 40 hours'}
            fontSize="16px"
            paddingTop="20px"
            onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
              setIsMaximumAllocation?.(event.target.checked);
              setIsMoreThanMaximumAllocation(event.target.checked);
            }}
          />
        </Box>
      </Box>

      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          paddingBottom: '15px',
          paddingLeft: '35px',
          paddingRight: '35px'
        }}
      >
        <Typography
          sx={{
            fontFamily: 'Be Vietnam Pro',
            fontWeight: 500,
            fontSize: '16px',
            color: '#74777F'
          }}
        >
          Project
        </Typography>
        <Typography
          sx={{
            fontFamily: 'Be Vietnam Pro',
            fontWeight: 500,
            fontSize: '16px',
            color: '#74777F',
            textAlign: 'center'
          }}
        >
          Current Allocation
          <Typography
            sx={{
              fontFamily: 'Be Vietnam Pro',
              fontWeight: 500,
              fontSize: '16px',
              color: '#74777F',
              textAlign: 'center'
            }}
          >
            (hours per week)
          </Typography>
        </Typography>
        <Typography
          sx={{
            fontFamily: 'Be Vietnam Pro',
            fontWeight: 500,
            fontSize: '16px',
            color: '#74777F',
            textAlign: 'center'
          }}
        >
          Suggested Allocation
          <Typography
            sx={{
              fontFamily: 'Be Vietnam Pro',
              fontWeight: 500,
              fontSize: '16px',
              color: '#74777F',
              textAlign: 'center'
            }}
          >
            (hours per week)
          </Typography>
        </Typography>
      </Box>

      <Box
        sx={{
          display: 'flex',
          justifyContent: 'center',
          paddingBottom: '15px'
        }}
      >
        <Box
          sx={{
            height: '1px',
            background: '#CAC4D0',
            width: '518px'
          }}
        />
      </Box>

      <Box sx={{ padding: '0px 35px' }}>
        {loading ? (
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              pointerEvents: 'none'
            }}
          >
            <CircularProgress />
          </Box>
        ) : (
          <ProjectMemberAllocationGrid
            pendingRequest={pendingRequest}
            members={currentAllocationProjects}
            isAdd={isAdd}
            unit={unit}
            setSuggestedAllocation={
              setSuggestedAllocation as (value: string | undefined) => void
            }
            value={value}
            isMaximumValue={isMoreThanMaximumAllocation}
            setSuggestedAllocationList={setSuggestedAllocationList}
          />
        )}
      </Box>
      <Box
        sx={{
          display: 'flex',
          marginTop: '5px',
          marginRight: '5px',
          justifyContent: 'flex-end'
        }}
      >
        {pendingRequest && (
          <ApprovalText color="#D1DCFB" label="Waiting for Approval" />
        )}
      </Box>
      <Box
        sx={{
          display: 'flex',
          marginTop: '5px',
          marginRight: '5px',
          justifyContent: 'center'
        }}
      >
        {checkApprovalForOthers && !pendingRequest && (
          <ApprovalText
            color="#D1DCFB"
            label="Waiting Approval for Another Project"
          />
        )}
      </Box>
    </Box>
  );
};

export default Allocation;
